import React, { useEffect, useRef } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import {
	isSearchAssetsPartial,
	isSearchAtlasPartial,
	isSearchCompassPartial,
	isSearchConfluencePartial,
	isSearchJiraPartial,
	isSearchMercuryPartial,
	isSearchResultFederated,
	isSearchResultGraphEntity,
	isSearchResultJiraIssue,
	isSearchResultJiraProject,
	isSearchTalentPartial,
	isSearchTrelloPartial,
	mapJiraIssuePartial,
	mapJiraProjectPartial,
	type SearchPageNode,
} from '@atlassian/search-client';

import { ApplicationModes } from '../../../../common/constants/application-modes';
import { BulkConnectBanner } from '../../../../common/ui/bulk-connect-banner';
import { SearchResultSkeleton } from '../../../../common/ui/search-result-skeleton';
import { serve3PBulkConnectWithoutExposure } from '../../../../common/utils/experiments';
import { OAuthContainer } from '../../../../common/utils/oauth-container';
import { useAnalytics, useCommonAndPrivateAttributes } from '../../../../controllers/analytics';
import {
	useBulkConnectModalOpenState,
	useNewlyConnectedProducts,
} from '../../../../controllers/bulk-connect';
import useDwellTime from '../../../../controllers/dwell';
import { useAreLLMPoweredFeaturesEnabled } from '../../../../controllers/entitlement-checks';
import { ErrorBoundary } from '../../../../controllers/error-boundary';
import { useOAuthHandlers } from '../../../../controllers/oauth';
import { useApplicationMode, useBootstrap, useIsHello } from '../../../../controllers/store/bootstrap';
import { useFetchFromCursor, useIsLoadingMore, usePageEndCursor, useResults, useSearchActions, useSelectedProduct } from '../../../../controllers/store/search';

import { LeapQueryWithErrorBoundary } from './leap-query';
import { AssetsSearchResult } from './product-results/assets';
import { AtlasSearchResult } from './product-results/atlas';
import { CompassSearchResult } from './product-results/compass';
import { ConfluenceSearchResult } from './product-results/confluence';
import { JiraSearchResult } from './product-results/jira';
import { isSearchJiraDcPartial, JiraDcSearchResult } from './product-results/jira-dc';
import { MercurySearchResult } from './product-results/mercury';
import { ThirdPartySearchResult } from './product-results/third-party';
import { ThirdPartyFederatedSearchResult } from './product-results/third-party-federated';
import { TrelloSearchResult } from './product-results/trello';

const resultsListGridAreaStyles = xcss({
	gridArea: 'results-list',
	display: 'flex',
	flexDirection: 'column',
	gap: 'space.300',
});

const noMoreResultsStyles = xcss({
	marginBottom: 'space.400',
});

// How many search results to show before the OAuth banner
const RELATED_ENTITIES_BANNER_CUTOFF = 3;
const OAUTH_BANNER_CUTOFF = 4;
const LEAP_QUERY_BANNER_CUTOFF = 5;

type ResultRecordProps = {
	node: SearchPageNode;
	index: number;
};

export const ResultRecord = ({ node, index }: ResultRecordProps): React.JSX.Element | null => {
	if (!node) {
		throw new Error('ResultRecord: node is undefined');
	}
	if (isSearchConfluencePartial(node)) {
		return <ConfluenceSearchResult partial={node} position={index} />;
	}
	if (isSearchResultJiraIssue(node)) {
		return <JiraSearchResult partial={mapJiraIssuePartial(node)} position={index} />;
	}
	if (isSearchResultJiraProject(node)) {
		return <JiraSearchResult partial={mapJiraProjectPartial(node)} position={index} />;
	}
	if (isSearchJiraPartial(node)) {
		return <JiraSearchResult partial={node} position={index} />;
	}
	if (isSearchAtlasPartial(node)) {
		return <AtlasSearchResult partial={node} position={index} />;
	}
	if (isSearchCompassPartial(node)) {
		return <CompassSearchResult partial={node} position={index} />;
	}
	if (isSearchTrelloPartial(node)) {
		return <TrelloSearchResult partial={node} position={index} />;
	}
	if (isSearchMercuryPartial(node)) {
		return <MercurySearchResult partial={node} position={index} />;
	}
	if (isSearchJiraDcPartial(node)) {
		return <JiraDcSearchResult partial={node} position={index} />;
	}
	if (isSearchResultFederated(node)) {
		return <ThirdPartyFederatedSearchResult partial={node} position={index} />;
	}
	if (isSearchAssetsPartial(node) && fg('assets_rovo_search_all')) {
		return <AssetsSearchResult partial={node} position={index} />;
	}
	if (isSearchResultGraphEntity(node)) {
		// Leave this one last
		return <ThirdPartySearchResult partial={node} position={index} />;
	}
	if (isSearchTalentPartial(node)) {
		/**
		 * This is a temporary fix to avoid the never ending throw causing the search page to continously re-render
		 * This is becasue Talent results will not be supported in the search page in favor of Relay Search Results.
		 */
		return null;
	}
	throw new Error(`ResultRecord: unsupported node type ${node.__typename}`);
};

export const ResultRecordWithErrorBoundary = ({
	fallback = null,
	...props
}: ResultRecordProps & { fallback?: React.ReactNode }): React.JSX.Element => (
	<ErrorBoundary fallback={fallback} section="SearchPageResultRecord">
		<ResultRecord {...props} />
	</ErrorBoundary>
);

export const Results = (): React.JSX.Element => {
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const [results] = useResults();
	const { setFetchFromCursor } = useSearchActions();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const currentCursor = useFetchFromCursor();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const nextFetchCursor = usePageEndCursor();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const isLoadingMore = useIsLoadingMore();
	const hasNextPage = nextFetchCursor && currentCursor !== nextFetchCursor;
	const [selectedProduct] = useSelectedProduct();
	const lastResultRef = useRef<HTMLDivElement | null>(null);
	const applicationMode = useApplicationMode();
	const areLLMFeaturesEnabled = useAreLLMPoweredFeaturesEnabled();

	const [{ thirdPartyConfigs, products: availableProducts }] = useBootstrap();
	const [isHello] = useIsHello();
	const { fireAnalyticsScreenEvent, fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes } = useCommonAndPrivateAttributes();
	const { getNewlyConnectedProducts } = useNewlyConnectedProducts();

	useDwellTime();

	useEffect(() => {
		const nextPageObserver = new IntersectionObserver((entries) => {
			if (entries[0].isIntersecting) {
				if (nextFetchCursor && nextFetchCursor !== currentCursor) {
					setFetchFromCursor(nextFetchCursor);
				}
			}
		}, {});
		if (lastResultRef.current) {
			nextPageObserver.observe(lastResultRef.current);
		}
	}, [currentCursor, nextFetchCursor, results, setFetchFromCursor]);

	if (!results) {
		return <></>;
	}

	const allResults = {
		...results,
		edges: results.edges.map((edge, index) => ({
			...edge,
			index,
		})),
	};

	const initialResults = allResults.edges.slice(0, RELATED_ENTITIES_BANNER_CUTOFF);
	const beforeOAuthBannerResults = allResults.edges.slice(
		RELATED_ENTITIES_BANNER_CUTOFF,
		OAUTH_BANNER_CUTOFF,
	);
	const beforeLeapQueryResults = allResults.edges.slice(
		OAUTH_BANNER_CUTOFF,
		LEAP_QUERY_BANNER_CUTOFF,
	);
	const remainingResults = allResults.edges.slice(LEAP_QUERY_BANNER_CUTOFF);

	const showLeapQuery = applicationMode === ApplicationModes.Rovo && areLLMFeaturesEnabled;

	return (
		<Box
			xcss={[resultsListGridAreaStyles, noMoreResultsStyles]}
			role="list"
			testId="search-page-results-list"
		>
			{initialResults.map(({ node, index }) => (
				<ResultRecordWithErrorBoundary node={node} index={index} key={node.id} />
			))}
			{beforeOAuthBannerResults.map(({ node, index }) => (
				<ResultRecordWithErrorBoundary node={node} index={index} key={node.id} />
			))}
			{!selectedProduct &&
				(serve3PBulkConnectWithoutExposure() ? (
					<ErrorBoundary section="SearchPageResultsBulkConnectBanner">
						<Box role="listitem">
							<BulkConnectBanner
								thirdPartyConfigs={thirdPartyConfigs}
								availableProducts={availableProducts}
								getNewlyConnectedProducts={getNewlyConnectedProducts}
								useBulkConnectModalOpenState={useBulkConnectModalOpenState}
								fireAnalyticsScreenEvent={fireAnalyticsScreenEvent}
								fireAnalyticsEvent={fireAnalyticsEvent}
								commonAttributes={commonAttributes}
							/>
						</Box>
					</ErrorBoundary>
				) : (
					<ErrorBoundary section="SearchPageResultsOAuthContainer">
						<Box role="listitem">
							<OAuthContainer
								thirdPartyConfigs={thirdPartyConfigs}
								availableProducts={availableProducts}
								isHello={isHello}
								useOAuthHandlers={useOAuthHandlers}
								fireAnalyticsEvent={fireAnalyticsEvent}
							/>
						</Box>
					</ErrorBoundary>
				))}
			{beforeLeapQueryResults.map(({ node, index }) => (
				<ResultRecordWithErrorBoundary node={node} index={index} key={node.id} />
			))}
			{showLeapQuery && (
				<Box role="listitem">
					<LeapQueryWithErrorBoundary />
				</Box>
			)}
			{remainingResults.length > 0 &&
				remainingResults.map(({ node, index }) => (
					<ResultRecordWithErrorBoundary node={node} index={index} key={node.id} />
				))}
			{(hasNextPage || isLoadingMore) && (
				<Box ref={lastResultRef}>
					<SearchResultSkeleton />
				</Box>
			)}
		</Box>
	);
};
